import { useQuery } from "@tanstack/react-query";
import { CirclePlay } from "lucide-react";
import { useTranslate } from "ra-core";
import { useSearchParams } from "react-router-dom";

import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { getSupabaseClient } from "../providers/supabase/supabase";

const BUCKET = "help-videos";
// Signed URLs outlive a long viewing session; the page refetches them before.
const URL_TTL_SECONDS = 4 * 60 * 60;
const REFRESH_MS = 3 * 60 * 60 * 1000;

interface HelpVideo {
  /** Object name in the bucket, without extension (also the poster name). */
  slug: string;
  /** Key under crm.construnario.help.videos */
  key: string;
  /** Duration shown on the card, m:ss */
  duration: string;
}

// Recorded with informes/video-manual-crm/scripts (Playwright + ffmpeg).
const VIDEOS: HelpVideo[] = [
  { slug: "01-entrar-en-el-crm", key: "login", duration: "1:17" },
  { slug: "02-mi-dia", key: "my_day", duration: "1:21" },
  { slug: "03-tu-cartera", key: "portfolio", duration: "1:19" },
  { slug: "04-la-ficha-de-una-empresa", key: "company", duration: "1:36" },
  { slug: "05-anotar-una-gestion", key: "activity", duration: "1:15" },
  { slug: "06-el-directorio", key: "directory", duration: "1:23" },
  { slug: "00-manual-completo", key: "full", duration: "8:31" },
];

type SignedUrls = Record<string, { video: string; poster: string }>;

const signVideoUrls = async (): Promise<SignedUrls> => {
  const paths = VIDEOS.flatMap((v) => [
    `${v.slug}.mp4`,
    `posters/${v.slug}.jpg`,
  ]);
  const { data, error } = await getSupabaseClient()
    .storage.from(BUCKET)
    .createSignedUrls(paths, URL_TTL_SECONDS);
  if (error) throw error;
  const byPath = new Map(data.map((d) => [d.path, d.signedUrl]));
  return Object.fromEntries(
    VIDEOS.map((v) => [
      v.slug,
      {
        video: byPath.get(`${v.slug}.mp4`) ?? "",
        poster: byPath.get(`posters/${v.slug}.jpg`) ?? "",
      },
    ]),
  );
};

/**
 * Help: the "first steps" video manual. Videos live in a PRIVATE bucket
 * (they show real client data) and are played through short-lived signed URLs.
 * The selected chapter is kept in the URL (?v=<slug>) so it can be shared.
 */
export const HelpPage = () => {
  const translate = useTranslate();
  const [params, setParams] = useSearchParams();
  const current = VIDEOS.find((v) => v.slug === params.get("v")) ?? VIDEOS[0];

  const { data: urls, error } = useQuery({
    queryKey: ["help-videos"],
    queryFn: signVideoUrls,
    staleTime: REFRESH_MS,
    refetchInterval: REFRESH_MS,
  });

  const t = (video: HelpVideo, field: "title" | "description") =>
    translate(`crm.construnario.help.videos.${video.key}.${field}`);

  const select = (video: HelpVideo) => {
    setParams({ v: video.slug });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="mx-auto mt-4 flex max-w-5xl flex-col gap-6 pb-10">
      <div>
        <h1 className="text-2xl font-semibold">
          {translate("crm.construnario.help.title")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {translate("crm.construnario.help.intro")}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <div className="aspect-video w-full overflow-hidden rounded-xl border bg-black">
          {error || (urls && !urls[current.slug].video) ? (
            <div className="flex h-full items-center justify-center p-6 text-center text-sm text-white/80">
              {translate("crm.construnario.help.error")}
            </div>
          ) : urls ? (
            <video
              key={current.slug}
              className="h-full w-full"
              src={urls[current.slug].video}
              poster={urls[current.slug].poster}
              controls
              preload="metadata"
              playsInline
            />
          ) : (
            <Skeleton className="h-full w-full rounded-none" />
          )}
        </div>
        <div>
          <h2 className="text-lg font-semibold">{t(current, "title")}</h2>
          <p className="text-sm text-muted-foreground">
            {t(current, "description")}
          </p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {VIDEOS.map((video, index) => {
          const isCurrent = video.slug === current.slug;
          return (
            <button
              key={video.slug}
              type="button"
              onClick={() => select(video)}
              className="text-left"
              aria-current={isCurrent}
            >
              <Card
                className={cn(
                  "h-full gap-0 overflow-hidden p-0 transition-shadow hover:shadow-md",
                  isCurrent && "ring-2 ring-primary",
                )}
              >
                <div className="relative aspect-video bg-muted">
                  {urls?.[video.slug].poster ? (
                    <img
                      src={urls[video.slug].poster}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : null}
                  <CirclePlay className="absolute left-1/2 top-1/2 size-10 -translate-x-1/2 -translate-y-1/2 text-white drop-shadow-md" />
                  <span className="absolute bottom-2 right-2 rounded bg-black/70 px-1.5 py-0.5 text-xs text-white">
                    {video.duration}
                  </span>
                </div>
                <div className="flex flex-col gap-1 p-3">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">
                    {video.key === "full"
                      ? translate("crm.construnario.help.all_chapters")
                      : translate("crm.construnario.help.chapter", {
                          n: index + 1,
                        })}
                  </span>
                  <span className="font-medium">{t(video, "title")}</span>
                </div>
              </Card>
            </button>
          );
        })}
      </div>

      <p className="text-sm text-muted-foreground">
        {translate("crm.construnario.help.support")}
      </p>
    </div>
  );
};

HelpPage.path = "/help";
